import React, { useState, useEffect, useMemo } from 'react';
import { Provider, UserRole, Client } from '../types';
import {
  getCurrentPayPeriod,
  getAdjacentPayPeriod,
  getRecentPayPeriods,
  formatDisplayDate,
  AllegiantPayPeriod
} from '../utils/payPeriod';
import {
  X,
  UserCheck,
  Calendar,
  DollarSign,
  Briefcase,
  Users,
  CheckCircle2,
  Clock,
  Plus,
  Trash2,
  Edit2,
  Save,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ArrowLeft
} from 'lucide-react';

interface ProviderDetailModalProps {
  providerId: string;
  userRole: UserRole;
  onClose: () => void;
  onRefreshList: () => void;
  onEdit?: (provider: Provider) => void;
}

export const ProviderDetailModal: React.FC<ProviderDetailModalProps> = ({
  providerId,
  userRole,
  onClose,
  onRefreshList,
  onEdit
}) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [allClients, setAllClients] = useState<Client[]>([]);
  const [selectedClientIdToAdd, setSelectedClientIdToAdd] = useState('');
  
  // Canonical Allegiant Pay Period
  const [selectedPeriod, setSelectedPeriod] = useState<AllegiantPayPeriod>(() => getCurrentPayPeriod());
  const [isCustomRange, setIsCustomRange] = useState(false);
  const [startDate, setStartDate] = useState(selectedPeriod.startDate);
  const [endDate, setEndDate] = useState(selectedPeriod.endDate);

  const recentPeriods = useMemo(() => getRecentPayPeriods(6, 4), []);

  const handleSelectPeriod = (period: AllegiantPayPeriod) => {
    setSelectedPeriod(period);
    setStartDate(period.startDate);
    setEndDate(period.endDate);
    setIsCustomRange(false);
  };

  const handlePrevPeriod = () => {
    const prev = getAdjacentPayPeriod(selectedPeriod, -1);
    handleSelectPeriod(prev);
  };

  const handleNextPeriod = () => {
    const next = getAdjacentPayPeriod(selectedPeriod, 1);
    handleSelectPeriod(next);
  };

  // Rate Editing
  const [isEditingRates, setIsEditingRates] = useState(false);
  const [ratePerSession, setRatePerSession] = useState<number | ''>('');
  const [payLadder, setPayLadder] = useState<'STANDARD' | 'CF_PLUS_5'>('STANDARD');

  const isAdmin = userRole === 'ADMIN';

  // Mobile/PWA popstate back navigation support: hardware back closes modal without page exit
  useEffect(() => {
    if (typeof window !== 'undefined' && window.innerWidth <= 1024) {
      window.history.pushState({ providerDetailOpen: true }, '');
      const handlePopState = () => {
        onClose();
      };
      window.addEventListener('popstate', handlePopState);
      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [onClose]);

  const fetchProviderDetails = async () => {
    try {
      setLoading(true);
      const res = await fetch(`/api/providers/${providerId}/details?startDate=${startDate}&endDate=${endDate}`);
      if (res.ok) {
        const json = await res.json();
        setData(json);
        setRatePerSession(
          json.provider.rate_per_session !== null && json.provider.rate_per_session !== undefined
            ? json.provider.rate_per_session
            : ''
        );
        setPayLadder(json.provider.pay_ladder === 'CF_PLUS_5' ? 'CF_PLUS_5' : 'STANDARD');
      }
    } catch (err) {
      console.error('Error fetching provider details:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const res = await fetch('/api/clients');
      if (res.ok) {
        const json = await res.json();
        setAllClients(json);
      }
    } catch (err) {
      console.error('Error fetching clients:', err);
    }
  };

  useEffect(() => {
    fetchProviderDetails();
    fetchClients();
  }, [providerId, startDate, endDate]);

  const handleAssignClient = async () => {
    if (!selectedClientIdToAdd) return;
    try {
      const res = await fetch(`/api/providers/${providerId}/assignments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: selectedClientIdToAdd })
      });
      if (res.ok) {
        setSelectedClientIdToAdd('');
        await fetchProviderDetails();
        onRefreshList();
      }
    } catch (err) {
      console.error('Assign failed:', err);
    }
  };

  const handleUnassignClient = async (clientId: string) => {
    if (!confirm('Unassign this client from provider caseload?')) return;
    try {
      const res = await fetch(`/api/providers/${providerId}/assignments/${clientId}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        await fetchProviderDetails();
        onRefreshList();
      }
    } catch (err) {
      console.error('Unassign failed:', err);
    }
  };

  const handleSaveRates = async () => {
    try {
      const res = await fetch(`/api/providers/${providerId}/rates`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rate_per_session: ratePerSession !== '' ? Number(ratePerSession) : null,
          pay_ladder: payLadder
        })
      });
      if (res.ok) {
        setIsEditingRates(false);
        await fetchProviderDetails();
      }
    } catch (err) {
      console.error('Save rates failed:', err);
    }
  };

  if (loading && !data) {
    return (
      <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4">
        <div className="bg-white border border-slate-200/90 p-6 rounded-xl text-slate-600 text-xs shadow-xl">Loading provider profile...</div>
      </div>
    );
  }

  if (!data) return null;

  const { provider, supervisor, supervised, assignedClients, sessions, payPeriodSummary } = data;
  const unassignedClients = allClients.filter(c => !assignedClients.some((ac: any) => ac.id === c.id));

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-0 sm:p-4 overflow-y-auto">
      <div className="bg-white w-full h-full sm:h-auto sm:max-w-4xl sm:rounded-2xl rounded-none border-0 sm:border border-slate-200/90 shadow-xl overflow-hidden sm:my-8 sm:max-h-[90vh] flex flex-col">
        {/* Mobile Back Header Bar */}
        <div className="sm:hidden p-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="flex items-center space-x-2 text-slate-700 hover:text-slate-900 hover:bg-slate-100 rounded-lg px-2 py-1 text-xs font-semibold active:scale-95 transition-all cursor-pointer"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Providers</span>
          </button>
          <span className="text-[10px] font-mono text-slate-500">Provider Details</span>
        </div>

        {/* Header */}
        <div className="p-6 bg-white border-b border-slate-100 flex justify-between items-start">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 rounded-xl bg-sky-50 border border-sky-200 flex items-center justify-center text-sky-800 font-bold text-lg">
              {provider.name.split(' ').map((n: string) => n[0]).join('')}
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h2 className="text-lg font-bold text-slate-900">{provider.name}</h2>
                <span className="text-xs font-mono font-bold px-2 py-0.5 rounded bg-sky-50 text-sky-800 border border-sky-200">
                  {provider.role}
                </span>
                <span className="text-xs font-mono text-slate-500">ID: {provider.id}</span>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">{provider.title}</p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {isAdmin && onEdit && (
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onEdit(provider);
                }}
                className="px-2.5 py-1.5 rounded-lg bg-sky-50 hover:bg-sky-100 text-sky-800 border border-sky-200 text-xs font-semibold flex items-center space-x-1.5 transition-colors cursor-pointer"
                title="Edit Provider Profile & Clinical Email"
                id="modal-edit-provider-btn"
              >
                <Edit2 className="w-3.5 h-3.5" />
                <span>Edit Profile</span>
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="p-6 overflow-y-auto space-y-6 flex-1 text-xs">
          {/* Profile & Credentials Strip */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 bg-slate-50 p-4 rounded-xl border border-slate-200">
            <div>
              <span className="text-slate-600 block text-[10px] uppercase tracking-wider font-semibold">Credentials</span>
              <span className="text-slate-800 font-mono mt-0.5 block">
                NPI: {provider.npi || 'On File'}
              </span>
              <span className="text-slate-600 font-mono text-[11px] block">
                Lic: {provider.license_number || 'AZ-SLP-Active'}
              </span>
              <span className="font-mono text-[11px] block mt-0.5 text-slate-800">
                AHCCCS:{' '}
                {provider.ahcccs_id ? (
                  <span className="text-amber-800 font-medium">{provider.ahcccs_id}</span>
                ) : (
                  <span className="text-slate-500 italic">Not configured</span>
                )}
              </span>
            </div>

            <div>
              <span className="text-slate-600 block text-[10px] uppercase tracking-wider font-semibold">Clinical Supervisor</span>
              {supervisor ? (
                <div className="mt-0.5">
                  <span className="text-indigo-800 font-medium block">{supervisor.name} (CCC-SLP)</span>
                  <span className="text-slate-500 text-[10px]">Supervision Agreement Active</span>
                </div>
              ) : (
                <span className="text-slate-600 mt-0.5 block">Independent Practitioner</span>
              )}
            </div>

            <div>
              <span className="text-slate-600 block text-[10px] uppercase tracking-wider font-semibold">Contact & Account</span>
              <span className="text-slate-800 block mt-0.5 truncate">{provider.email || 'No email on file'}</span>
              <span className={`text-[10px] font-bold ${provider.active === 1 ? 'text-emerald-800' : 'text-amber-800'}`}>
                {provider.active === 1 ? 'Active Account' : 'Inactive Account'}
              </span>
            </div>

            <div>
              <span className="text-slate-600 block text-[10px] uppercase tracking-wider font-semibold">Caseload Status</span>
              <span className="text-sky-800 font-bold text-sm mt-0.5 block">{assignedClients.length} Assigned Clients</span>
              <span className="text-slate-500 text-[10px]">Pediatric Speech Therapy</span>
            </div>
          </div>

          {/* Supervised Providers (if supervisor) */}
          {supervised && supervised.length > 0 && (
            <div className="p-4 rounded-xl bg-indigo-50 border border-indigo-200 space-y-2">
              <div className="flex items-center space-x-2 text-indigo-800 font-bold">
                <UserCheck className="w-4 h-4" />
                <span>Supervised Clinicians & SLPAs ({supervised.length})</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {supervised.map((s: any) => (
                  <span key={s.id} className="px-2.5 py-1 rounded bg-white border border-slate-200 text-slate-800 font-mono text-[11px]">
                    {s.name} ({s.role})
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Caseload Management */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold uppercase tracking-widest text-slate-900 flex items-center space-x-2">
                <Users className="w-4 h-4 text-sky-700" />
                <span>Caseload Management ({assignedClients.length} Clients)</span>
              </h3>
            </div>

            {isAdmin && (
              <div className="flex items-center space-x-2 bg-slate-50 p-2.5 rounded-lg border border-slate-200">
                <select
                  value={selectedClientIdToAdd}
                  onChange={(e) => setSelectedClientIdToAdd(e.target.value)}
                  className="flex-1 bg-white text-slate-800 border border-slate-200 rounded-xl px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-slate-400"
                >
                  <option value="">Select a client to assign to {provider.name}...</option>
                  {unassignedClients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.last_name}, {c.first_name} (ID: {c.id} • {c.primary_payer_name})
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleAssignClient}
                  disabled={!selectedClientIdToAdd}
                  className="bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white font-bold px-3 py-1.5 rounded-lg text-xs transition-colors flex items-center space-x-1"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Assign Client</span>
                </button>
              </div>
            )}

            <div className="bg-white rounded-xl overflow-hidden border border-slate-200 divide-y divide-slate-100">
              {assignedClients.length === 0 ? (
                <div className="p-6 text-center text-slate-500">
                  No clients currently assigned to this provider. Use the selector above to assign clients.
                </div>
              ) : (
                assignedClients.map((c: any) => (
                  <div key={c.id} className="p-3.5 flex items-center justify-between hover:bg-slate-50 transition-colors">
                    <div>
                      <div className="font-semibold text-slate-800">
                        {c.last_name}, {c.first_name}
                      </div>
                      <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                        ID: {c.id} • DOB: {c.dob} • Payer: {c.primary_payer_name} • Assigned: {c.assigned_date}
                      </div>
                    </div>

                    {isAdmin && (
                      <button
                        onClick={() => handleUnassignClient(c.id)}
                        className="text-rose-600 hover:text-rose-700 p-1 rounded hover:bg-rose-50 transition-colors"
                        title="Unassign client from provider"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Sessions & Pay Period Calculation */}
          <div className="space-y-3 pt-4 border-t border-slate-100">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <h3 className="text-xs font-bold uppercase tracking-widest text-slate-900 flex items-center space-x-2">
                <DollarSign className="w-4 h-4 text-emerald-700" />
                <span>Sessions & Payroll Summary</span>
              </h3>

              <div className="flex flex-col items-end gap-1.5">
                <div className="flex items-center space-x-1.5 bg-slate-50 p-1 rounded-lg border border-slate-200 text-xs">
                  <button
                    onClick={handlePrevPeriod}
                    title="Previous Pay Period"
                    className="p-1 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-800 transition-colors"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </button>

                  <select
                    value={selectedPeriod.id}
                    onChange={(e) => {
                      const target = recentPeriods.find(p => p.id === e.target.value);
                      if (target) handleSelectPeriod(target);
                    }}
                    className="bg-white border border-slate-200 rounded-lg px-2 py-0.5 text-slate-800 font-mono text-[11px] focus:outline-none focus:ring-2 focus:ring-slate-400 cursor-pointer"
                  >
                    {recentPeriods.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.label} {p.isCurrent ? '★ (Current)' : ''}
                      </option>
                    ))}
                  </select>

                  <button
                    onClick={handleNextPeriod}
                    title="Next Pay Period"
                    className="p-1 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-800 transition-colors"
                  >
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>

                  <button
                    onClick={() => setIsCustomRange(!isCustomRange)}
                    className={`px-1.5 py-0.5 rounded text-[10px] font-semibold border ${
                      isCustomRange
                        ? 'bg-slate-100 text-slate-900 border-slate-300'
                        : 'bg-white text-slate-600 hover:text-slate-800 border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    {isCustomRange ? 'Standard' : 'Custom'}
                  </button>
                </div>

                <div className="flex items-center space-x-2 text-[10px] font-mono text-slate-500">
                  <span>Cutoff: <strong className="text-amber-800">{formatDisplayDate(selectedPeriod.cutoffDate)} (Fri)</strong></span>
                  <span>•</span>
                  <span>Pay: <strong className="text-emerald-800">{formatDisplayDate(selectedPeriod.payDate)} (Wed)</strong></span>
                </div>

                {isCustomRange && (
                  <div className="flex items-center space-x-1.5 pt-1">
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="bg-white border border-slate-200 rounded-lg px-1.5 py-0.5 text-slate-800 text-[10px] font-mono"
                    />
                    <span className="text-slate-500 text-xs">to</span>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="bg-white border border-slate-200 rounded-lg px-1.5 py-0.5 text-slate-800 text-[10px] font-mono"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Compensation Card */}
            <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 grid grid-cols-1 sm:grid-cols-4 gap-4">
              <div>
                <span className="text-slate-600 block text-[10px] uppercase font-bold tracking-wider">Completed Sessions</span>
                <span className="text-2xl font-bold text-slate-900 mt-1 block">
                  {payPeriodSummary?.completedSessions || 0}
                </span>
                <span className="text-[10px] text-slate-500">Full Session Payment</span>
              </div>

              <div>
                <span className="text-slate-600 block text-[10px] uppercase font-bold tracking-wider">Partial Sessions</span>
                <span className="text-2xl font-bold text-amber-800 mt-1 block">
                  {payPeriodSummary?.partialSessions || 0}
                </span>
                <span className="text-[10px] text-slate-500">Prorated By Portion</span>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600 block text-[10px] uppercase font-bold tracking-wider">Configured Rate</span>
                  {isAdmin && (
                    <button
                      onClick={() => setIsEditingRates(!isEditingRates)}
                      className="text-sky-800 hover:text-sky-900 hover:bg-sky-50 rounded px-1 text-[10px] flex items-center space-x-1"
                    >
                      <Edit2 className="w-3 h-3" />
                      <span>{isEditingRates ? 'Cancel' : 'Edit Rate'}</span>
                    </button>
                  )}
                </div>
                {isEditingRates ? (
                  <div className="space-y-1.5 mt-1">
                    <div className="flex items-center space-x-1">
                      <span className="text-[10px] text-slate-600">Ladder:</span>
                      <select
                        value={payLadder}
                        onChange={(e) => setPayLadder(e.target.value === 'CF_PLUS_5' ? 'CF_PLUS_5' : 'STANDARD')}
                        className="bg-white border border-slate-200 rounded-lg px-1.5 py-0.5 text-slate-800 text-xs"
                      >
                        <option value="STANDARD">STANDARD</option>
                        <option value="CF_PLUS_5">CF_PLUS_5</option>
                      </select>
                    </div>
                    <div className="flex items-center space-x-1">
                      <span className="text-[10px] text-slate-600">$/Sess:</span>
                      <input
                        type="number"
                        placeholder="Enter rate"
                        value={ratePerSession}
                        onChange={(e) => setRatePerSession(e.target.value === '' ? '' : Number(e.target.value))}
                        className="w-24 bg-white border border-slate-200 rounded-lg px-1.5 py-0.5 text-slate-800 text-xs font-mono"
                      />
                    </div>
                    <button
                      onClick={handleSaveRates}
                      className="bg-emerald-600 hover:bg-emerald-500 text-white px-2 py-0.5 rounded text-[10px] font-bold"
                    >
                      Save Rate
                    </button>
                  </div>
                ) : (
                  <div className="mt-1 font-mono text-slate-800">
                    {provider.rate_per_session !== null && provider.rate_per_session !== undefined && Number(provider.rate_per_session) > 0 ? (
                      <div className="text-sm font-bold text-emerald-800">${provider.rate_per_session} <span className="text-[10px] text-slate-500 font-normal">/ session (Custom Override)</span></div>
                    ) : provider.pay_ladder === 'CF_PLUS_5' ? (
                      <div className="text-xs text-sky-800 font-medium">CF Pay Schedule (Weekly Ladder $30–$90)</div>
                    ) : (
                      <div className="text-xs text-sky-800 font-medium">Standard Pay Schedule (Weekly Ladder $25–$85)</div>
                    )}
                  </div>
                )}
              </div>

              <div className="bg-white p-3 rounded-lg border border-slate-200 flex flex-col justify-center">
                <span className="text-emerald-800 block text-[10px] uppercase font-bold tracking-wider">Total Calculated Pay</span>
                <span className="text-xl font-bold text-emerald-800 font-mono mt-0.5 block">
                  ${Number(payPeriodSummary?.totalCalculatedPay || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                <span className="text-[10px] text-slate-500">For {startDate} to {endDate}</span>
              </div>
            </div>

            {/* Sessions Table / Mobile Cards */}
            <div className="bg-white rounded-xl overflow-hidden border border-slate-200">
              <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-[11px] font-bold text-slate-700 flex justify-between items-center">
                <span>Sessions in Pay Period ({sessions.length})</span>
                <span className="text-[10px] text-slate-500">Full: {payPeriodSummary?.completedSessions || 0} | Partial: {payPeriodSummary?.partialSessions || 0}</span>
              </div>

              {sessions.length === 0 ? (
                <div className="p-6 text-center text-slate-500 text-xs">
                  No sessions scheduled or logged in this pay period.
                </div>
              ) : (
                <>
                  {/* Desktop Table (md and up) */}
                  <div className="hidden md:block overflow-x-auto max-h-56 overflow-y-auto">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-slate-50 text-[10px] uppercase text-slate-600 border-b border-slate-200">
                        <tr>
                          <th className="px-4 py-2">DOS</th>
                          <th className="px-4 py-2">Client</th>
                          <th className="px-4 py-2">Code</th>
                          <th className="px-4 py-2">Duration</th>
                          <th className="px-4 py-2">Status</th>
                          <th className="px-4 py-2">Portion</th>
                          <th className="px-4 py-2">Session Payment</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {sessions.map((s: any) => (
                          <tr key={s.id} className="hover:bg-slate-50">
                            <td className="px-4 py-2 font-mono text-slate-700">{s.dos}</td>
                            <td className="px-4 py-2 text-slate-800">{s.first_name} {s.last_name}</td>
                            <td className="px-4 py-2 font-mono text-sky-800">{s.service_code}</td>
                            <td className="px-4 py-2 text-slate-500 font-mono">{s.duration_minutes || 60} min</td>
                            <td className="px-4 py-2">
                              <span className={`text-[10px] font-mono px-2 py-0.5 rounded font-bold ${
                                s.session_type === 'FULL'
                                  ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                                  : s.session_type === 'PARTIAL'
                                  ? 'bg-amber-50 text-amber-800 border border-amber-200'
                                  : 'bg-slate-100 text-slate-600 border border-slate-200'
                              }`}>
                                {s.session_type === 'FULL' ? 'COMPLETED (FULL)' : s.session_type === 'PARTIAL' ? 'PARTIAL' : s.status}
                              </span>
                            </td>
                            <td className="px-4 py-2 font-mono text-slate-700">
                              {s.session_portion > 0 ? `${Math.round(s.session_portion * 100)}%` : '—'}
                            </td>
                            <td className="px-4 py-2 font-mono">
                              {s.documentationEligible === false ? (
                                <span className="text-cyan-800 font-semibold" title={s.documentationHoldReason || 'Not included in payable total'}>
                                  Excluded{s.session_payment > 0 ? ` ($${Number(s.session_payment).toFixed(2)})` : ''}
                                </span>
                              ) : s.session_payment > 0 ? (
                                <span className="text-emerald-800 font-semibold">
                                  +${Number(s.session_payment).toFixed(2)}
                                </span>
                              ) : (
                                <span className="text-slate-500">
                                  {provider.rate_per_session ? '$0.00' : 'Rate pending'}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Stacked Session Cards (Under md) */}
                  <div className="md:hidden divide-y divide-slate-100 max-h-72 overflow-y-auto">
                    {sessions.map((s: any) => (
                      <div key={`mb-sess-${s.id}`} className="p-3 space-y-1.5 bg-slate-50 text-xs">
                        {/* Line 1: Client & Payment */}
                        <div className="flex items-start justify-between gap-2">
                          <div className="font-semibold text-slate-900 text-xs">
                            {s.first_name} {s.last_name}
                          </div>
                          <div className="text-right shrink-0">
                            {s.documentationEligible === false ? (
                              <span className="text-cyan-800 font-mono font-bold text-xs" title={s.documentationHoldReason || 'Not included in payable total'}>
                                Excluded
                              </span>
                            ) : s.session_payment > 0 ? (
                              <span className="text-emerald-800 font-mono font-bold text-xs">
                                +${Number(s.session_payment).toFixed(2)}
                              </span>
                            ) : (
                              <span className="text-slate-500 text-[10px]">
                                {provider.rate_per_session ? '$0.00' : 'Rate pending'}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Line 2: DOS · CPT · Duration · Portion */}
                        <div className="text-[11px] text-slate-500 font-mono flex items-center gap-1.5 flex-wrap">
                          <span className="text-slate-700">{s.dos}</span>
                          <span className="text-slate-300">·</span>
                          <span className="text-sky-800 font-semibold">{s.service_code}</span>
                          <span className="text-slate-300">·</span>
                          <span>{s.duration_minutes || 60}m</span>
                          {s.session_portion > 0 && (
                            <>
                              <span className="text-slate-300">·</span>
                              <span className="text-slate-700">{Math.round(s.session_portion * 100)}% portion</span>
                            </>
                          )}
                        </div>

                        {/* Line 3: Status Badge */}
                        <div className="pt-0.5">
                          <span className={`text-[9.5px] font-mono px-2 py-0.5 rounded font-bold inline-block leading-none ${
                            s.session_type === 'FULL'
                              ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                              : s.session_type === 'PARTIAL'
                              ? 'bg-amber-50 text-amber-800 border border-amber-200'
                              : 'bg-slate-100 text-slate-600 border border-slate-200'
                          }`}>
                            {s.session_type === 'FULL' ? 'COMPLETED (FULL)' : s.session_type === 'PARTIAL' ? 'PARTIAL' : s.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 bg-slate-50 border-t border-slate-100 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-1.5 rounded-lg bg-white hover:bg-slate-100 text-slate-800 border border-slate-200 font-semibold text-xs transition-colors"
          >
            Close Provider Profile
          </button>
        </div>
      </div>
    </div>
  );
};
